import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Seo, breadcrumbSchema, faqSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHead, FaqList, CtaBand, RelatedLinks, Steps } from "@/components/Blocks";
import { WhatsAppIcon } from "@/components/Icon";
import { industryBySlug } from "@/data/industries";
import { PROCESS } from "@/data/home";
import { waLink } from "@/data/site";
import NotFound from "@/pages/NotFound";

const ListBlock = ({ eyebrow, title, items, testId }) => (
  <div data-testid={testId}>
    <p className="eyebrow">{eyebrow}</p>
    <h3 className="mt-4 font-display text-2xl font-bold">{title}</h3>
    <ul className="mt-6 border-t border-charcoal/15">
      {items.map((t) => <li key={t} className="flex gap-3 border-b border-charcoal/15 py-4 text-[15px]"><Check className="mt-0.5 h-4 w-4 shrink-0 text-rust-deep" />{t}</li>)}
    </ul>
  </div>
);

export default function IndustryDetail() {
  const { slug } = useParams();
  const ind = industryBySlug[slug];
  if (!ind) return <NotFound />;
  const path = `/industries/${ind.slug}`;
  const crumbs = [["Home", "/"], ["Industries", "/industries"], [ind.name, path]];
  const split = ind.h1.indexOf(" for ");
  const lines = split > 0 ? [ind.h1.slice(0, split), ind.h1.slice(split + 1)] : [ind.h1];

  return (
    <>
      <Seo title={ind.metaTitle} description={ind.metaDesc} path={path} jsonLd={[breadcrumbSchema(crumbs), faqSchema(ind.faqs)]} />
      <PageHero crumbs={crumbs} eyebrow={`Industry · ${ind.name}`} lines={lines} intro={ind.intro} img={ind.img} imgAlt={`${ind.name} production environment`}>
        <Link to={`/contact?industry=${encodeURIComponent(ind.name)}#inquiry`} className="btn-primary" data-testid="industry-hero-cta">Discuss Your Project <ArrowRight className="h-4 w-4" /></Link>
        <a href={waLink(`Hello Industrix Solution, I run a ${ind.name.toLowerCase()} business and would like to discuss a project.`)} target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="industry-hero-whatsapp"><WhatsAppIcon className="h-4 w-4" /> WhatsApp</a>
      </PageHero>

      <section className="wrap py-24 lg:py-32" data-testid="industry-challenges">
        <div className="grid gap-14 lg:grid-cols-2">
          <SectionHead eyebrow="Industry context" title={`Common challenges in ${ind.name.toLowerCase()}`} lead={ind.context} />
          <ol className="border-t border-charcoal/15">
            {ind.challenges.map((c, i) => (
              <Reveal as="li" key={c} delay={i * 0.05} className="flex gap-5 border-b border-charcoal/15 py-5">
                <span className="font-mono text-xs text-rust-ink pt-1">{String(i + 1).padStart(2, "0")}</span><span className="text-[16px]">{c}</span>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-t border-stone bg-white py-24 lg:py-32" data-testid="industry-solutions">
        <div className="wrap">
          <SectionHead eyebrow="Relevant digital solutions" title="Where technology makes the biggest difference" />
          <div className="mt-14 grid gap-4 md:grid-cols-2">
            {ind.solutions.map(([t, d, link], i) => (
              <Reveal key={t} delay={(i % 2) * 0.08}>
                <Link to={`/services/${link}`} className="group flex h-full flex-col rounded-md border border-stone bg-ivory p-8 transition-[border-color,box-shadow] duration-300 hover:border-charcoal/30 hover:shadow-[0_25px_50px_-30px_rgba(39,41,35,.45)]" data-testid={`industry-solution-${i}`}>
                  <span className="font-mono text-xs text-rust-ink">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-6 font-display text-2xl font-bold">{t}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-graphite">{d}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-rust-ink">Related service <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="wrap grid gap-14 py-24 md:grid-cols-2 lg:grid-cols-3 lg:py-32" data-testid="industry-usecases">
        <ListBlock testId="industry-software-usecases" eyebrow="Software use cases" title="Industry-specific applications" items={ind.useCases} />
        <ListBlock testId="industry-digital" eyebrow="Digital growth" title="Website & marketing opportunities" items={ind.digital} />
        <ListBlock testId="industry-integrations" eyebrow="Integration" title="Custom integration possibilities" items={ind.integrations} />
      </section>

      <section className="border-t border-stone bg-[#F1EEE7] py-24 lg:py-32" data-testid="industry-process">
        <div className="wrap">
          <SectionHead eyebrow="Implementation process" title="How a project with us runs" />
          <div className="mt-14"><Steps steps={PROCESS} /></div>
        </div>
      </section>

      <section className="wrap py-24 lg:py-32" data-testid="industry-faq">
        <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHead eyebrow="FAQ" title={`${ind.name}: frequently asked questions`} />
          <FaqList faqs={ind.faqs} idPrefix="industry-faq" />
        </div>
      </section>

      <CtaBand title={`Modernise your ${ind.name.toLowerCase()} business`} text="Tell us about your plant, products and current systems. Send your inquiry by WhatsApp or email and we will respond with suggested next steps." />
      <RelatedLinks services={ind.relServices} industries={ind.relIndustries} />
    </>
  );
}
